import React, { useState, useEffect } from 'react';
import { Play, Pause, Clock, DollarSign, FolderKanban, FileText, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimerWidget = ({ visible = true }) => {
  const [currentTimer, setCurrentTimer] = useState(null);
  const [taskInfo, setTaskInfo] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [earnings, setEarnings] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showStopDialog, setShowStopDialog] = useState(false);
  const [stopNotes, setStopNotes] = useState('');
  const [proofFile, setProofFile] = useState(null);

  useEffect(() => {
    fetchCurrentTimer();
    const interval = setInterval(fetchCurrentTimer, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let interval;
    if (currentTimer) {
      interval = setInterval(() => {
        const startTime = new Date(currentTimer.start_time);
        const now = new Date();
        const diffMs = now - startTime;
        const minutes = diffMs / 60000;
        setElapsed(minutes);
        setEarnings((minutes / 60) * currentTimer.hourly_rate);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentTimer]);

  const fetchCurrentTimer = async () => {
    try {
      const response = await axios.get(`${API}/time/current`);
      if (response.data) {
        setCurrentTimer(response.data);
        
        // Fetch task and project info
        const startTime = new Date(response.data.start_time);
        const now = new Date();
        const diffMs = now - startTime;
        const minutes = diffMs / 60000;
        setElapsed(minutes);
        setEarnings((minutes / 60) * response.data.hourly_rate);
        
        // Get task details
        fetchTaskInfo(response.data.task_id, response.data.project_id);
      } else {
        setCurrentTimer(null);
        setTaskInfo(null);
        setProjectInfo(null);
      }
    } catch (error) {
      console.error('Error fetching timer:', error);
    }
  };

  const fetchTaskInfo = async (taskId, projectId) => {
    try {
      const [tasksRes, projectRes] = await Promise.all([
        axios.get(`${API}/tasks/project/${projectId}`),
        axios.get(`${API}/projects/${projectId}`)
      ]);
      
      const task = tasksRes.data.find(t => t.id === taskId);
      setTaskInfo(task);
      setProjectInfo(projectRes.data);
    } catch (error) {
      console.error('Error fetching task/project info:', error);
    }
  };

  const handleStopClick = () => {
    setShowStopDialog(true);
  };

  const stopTimer = async () => {
    if (!currentTimer) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/time/stop`, { 
        entry_id: currentTimer.id,
        notes: stopNotes || null
      });
      
      toast.success(`Timer arrêté - ${formatTime(elapsed)} travaillées`);
      setCurrentTimer(null);
      setTaskInfo(null);
      setProjectInfo(null);
      setElapsed(0);
      setEarnings(0);
      setShowStopDialog(false);
      setStopNotes('');
      setProofFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes * 60) % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
    }
  };

  if (!visible || !currentTimer) return null;

  return (
    <>
      <div 
        className={cn(
          "fixed bottom-6 right-6 z-50 animate-slide-up",
          "bg-[var(--color-primary)] text-white rounded-2xl",
          "shadow-2xl border border-[var(--color-accent)]/30",
          "p-4 min-w-[320px]"
        )}
        data-testid="timer-widget"
      >
        {/* Project & Task info */}
        <div className="flex items-start gap-3 mb-3 pb-3 border-b border-white/20">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <FolderKanban className="w-5 h-5 text-[var(--color-accent)]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-white/60 truncate">
              {projectInfo?.name || 'Chargement...'}
            </p>
            <p className="font-medium text-white truncate" data-testid="task-name">
              {taskInfo?.name || 'Tâche en cours'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-[var(--color-accent)] rounded-full animate-pulse" />
          </div>
        </div>

        {/* Timer & Earnings */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--color-accent)]" />
              <span className="font-mono text-xl font-bold tracking-wider" data-testid="timer-display">
                {formatTime(elapsed)}
              </span>
            </div>
            
            <div className="border-l border-white/20 pl-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[var(--color-accent)]" />
              <span className="font-mono font-bold text-lg" data-testid="earnings-display">
                {earnings.toFixed(2)}€
              </span>
            </div>
          </div>
          
          <Button
            onClick={handleStopClick}
            disabled={loading}
            size="icon"
            className="bg-red-500 hover:bg-red-600 rounded-full h-10 w-10"
            data-testid="stop-timer-btn"
          >
            <Pause className="h-4 w-4" />
          </Button>
        </div>

        {/* Hourly rate */}
        <div className="mt-2 text-xs text-white/50 text-right">
          Taux: {currentTimer?.hourly_rate || 0}€/h
        </div>
      </div>

      {/* Stop Timer Dialog */}
      <Dialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[var(--color-accent)]" />
              Arrêter le timer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Summary */}
            <div className="p-4 bg-[#f8fafc] rounded-xl space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#64748b]">Projet</span>
                <span className="font-medium text-[var(--color-primary)]">{projectInfo?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#64748b]">Tâche</span>
                <span className="font-medium text-[var(--color-primary)]">{taskInfo?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#64748b]">Durée</span>
                <span className="font-mono font-bold text-[var(--color-primary)]">{formatTime(elapsed)}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-[#e2e8f0] pt-2 mt-2">
                <span className="text-[#64748b]">Gains</span>
                <span className="font-mono font-bold text-[var(--color-accent)]">{earnings.toFixed(2)}€</span>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (facultatif)</Label>
              <Textarea
                value={stopNotes}
                onChange={(e) => setStopNotes(e.target.value)}
                placeholder="Décrivez ce que vous avez fait..."
                className="input-default min-h-[80px]"
              />
            </div>

            {/* Proof upload */}
            <div className="space-y-2">
              <Label>Preuve de travail (facultatif)</Label>
              <div className="border-2 border-dashed border-[#e2e8f0] rounded-xl p-4 text-center">
                {proofFile ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-[var(--color-accent)]" />
                      <span className="text-sm text-[var(--color-primary)]">{proofFile.name}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setProofFile(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx"
                    />
                    <div className="flex flex-col items-center gap-2 text-[#64748b]">
                      <Upload className="w-8 h-8" />
                      <span className="text-sm">Cliquez pour ajouter un fichier</span>
                      <span className="text-xs">Capture d'écran, document...</span>
                    </div>
                  </label>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowStopDialog(false)}
              >
                Annuler
              </Button>
              <Button 
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={stopTimer}
                disabled={loading}
              >
                {loading ? 'Arrêt...' : 'Confirmer l\'arrêt'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TimerWidget;
